/* eslint-disable jsx-a11y/role-supports-aria-props */
import { useEffect, useRef, useState } from "react";

interface Props {
  label: string;
  value: string;
  placeholder: string;
  error?: string;
  onChange: (value: string) => void;
  onSelect: (value: string) => void;
  autocomplete: any;
}

const LocationField: React.FC<Props> = ({
  label,
  value,
  placeholder,
  error,
  onChange,
  onSelect,
  autocomplete,
}) => {
  const {
    query,
    setQuery,
    suggestions,
    isLoading,
    selectPlace,
    setIsEditing,
  } = autocomplete;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const [highlightIndex, setHighlightIndex] = useState<number>(-1);

  /* Sync value only when it changes externally */
  useEffect(() => {
    setQuery(value || "");
  }, [value, setQuery]);

  /* Close dropdown when clicking outside */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setIsEditing(false);
        setHighlightIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setIsEditing]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!suggestions.length) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;

      case "ArrowUp":
        e.preventDefault();
        setHighlightIndex((prev) =>
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;

      case "Enter":
        if (highlightIndex >= 0) {
          const selected = suggestions[highlightIndex];
          selectPlace(selected.place_id, onSelect);
          setIsEditing(false);
        }
        break;

      case "Escape":
        setIsEditing(false);
        setHighlightIndex(-1);
        break;
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <label className="block text-gray-600 mb-1">{label}</label>

      <input
        value={query}
        onFocus={() => setIsEditing(true)}
        onChange={(e) => {
          const val = e.target.value;
          setQuery(val);
          onChange(val);
          setIsEditing(true);
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={`input-field px-4 ${
          error ? "border-red-500 focus:ring-red-500" : ""
        }`}
        aria-autocomplete="list"
        aria-expanded={suggestions.length > 0}
      />

      {/* Dropdown */}
      {suggestions.length > 0 && (
        <ul className="absolute z-50 bg-white border w-full rounded-lg shadow mt-1 max-h-60 overflow-auto">
          {suggestions.map((item: any, index: number) => (
            <li
              key={item.place_id}
              onMouseDown={() =>
                selectPlace(item.place_id, onSelect)
              }
              className={`px-4 py-2 cursor-pointer text-sm
                ${
                  index === highlightIndex
                    ? "bg-gray-100"
                    : "hover:bg-gray-50"
                }
              `}
            >
              {item.description}
            </li>
          ))}
        </ul>
      )}

      {/* Loading */}
      {isLoading && (
        <p className="text-sm text-gray-400 mt-1">جاري البحث...</p>
      )}

      {/* No results */}
      {!isLoading && query && suggestions.length === 0 && (
        <p className="text-sm text-gray-400 mt-1">لا توجد نتائج</p>
      )}

      {/* Error */}
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

export default LocationField;
